import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRouteMatch } from '@tyro/core';
import { memo, useCallback } from 'react';
import { useDocumentationRelatedPages } from '../../api/pages';
import { useRecentlyViewedDocs } from '../../hooks/use-recently-viewed-docs';
import {
  useReaderActions,
  useReaderState,
} from '../../store/documentation-reader-provider';
import { ForThisPageCallout } from './for-this-page-callout';
import { ReaderCategoriesSection } from './reader-categories-section';
import { ReaderRecentlyViewedSection } from './reader-recently-viewed-section';

const CATEGORY_LIMIT = { default: 12, xl: 30 } as const;
const RELATED_LIMIT = { default: 5, xl: 8 } as const;
const RECENT_LIMIT = { default: 3, xl: 5 } as const;

// Memoised: it takes no props and reads only stable/low-frequency context,
// so it won't re-render while the user types in the search box above it
// (ReaderSearch re-renders per keystroke and renders this when query < 2).
export const ReaderHome = memo(function ReaderHome() {
  const routePattern = useRouteMatch();

  const { navigate } = useReaderActions();
  const { spaces: spacesData } = useReaderState();

  const { data: relatedPages } = useDocumentationRelatedPages(
    { routePattern },
    { enabled: routePattern != null },
  );

  const { entries: recentlyViewed, clear: onClearRecentlyViewed } =
    useRecentlyViewedDocs();

  // Hide spaces with no published content — an empty category card is
  // confusing for end users and gives them nowhere to go on click.
  const spaces = (spacesData ?? []).filter(
    (space) => space.articleCount > 0 || space.videoCount > 0,
  );

  const isXLViewport = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up('xl'),
  );
  const categoryLimit = isXLViewport
    ? CATEGORY_LIMIT.xl
    : CATEGORY_LIMIT.default;
  const relatedLimit = isXLViewport ? RELATED_LIMIT.xl : RELATED_LIMIT.default;
  const recentLimit = isXLViewport ? RECENT_LIMIT.xl : RECENT_LIMIT.default;
  const hasRelatedPages = relatedPages != null && relatedPages.length > 0;

  const onSelectSpace = useCallback(
    (spaceId: number) => navigate({ view: 'space', spaceId }),
    [navigate],
  );
  const onSelectPage = useCallback(
    (spaceId: number, pageSlug: string) =>
      navigate({ view: 'page', spaceId, pageSlug }),
    [navigate],
  );

  return (
    <Stack sx={{ px: 3, py: 2.5, gap: 3 }}>
      {hasRelatedPages && (
        <ForThisPageCallout
          relatedPages={relatedPages}
          collapsedLimit={relatedLimit}
          onSelectPage={onSelectPage}
        />
      )}

      <ReaderCategoriesSection
        spaces={spaces}
        collapsedLimit={categoryLimit}
        onSelectSpace={onSelectSpace}
      />

      {recentlyViewed.length > 0 && (
        <ReaderRecentlyViewedSection
          entries={recentlyViewed}
          collapsedLimit={recentLimit}
          onClear={onClearRecentlyViewed}
          onSelectPage={onSelectPage}
        />
      )}
    </Stack>
  );
});
